import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { connectionAPI, marketplaceAPI, getServerURL } from '../services/api';
import { ArrowLeft, MapPin, Building2, UserPlus, UserCheck, Clock, Package, Mail, Phone } from 'lucide-react';

const DealerProfilePage = () => {
  const { dealerId } = useParams();
  const navigate = useNavigate();
  const [dealer, setDealer] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [dealerId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch dealer profile
      const profileResponse = await connectionAPI.getProfile(dealerId);
      setDealer(profileResponse.data);

      // Fetch dealer's marketplace listings
      const listingsResponse = await marketplaceAPI.getAll({ dealer_id: dealerId });
      setListings(listingsResponse.data.watches || []);
    } catch (error) {
      console.error('Error fetching dealer profile:', error);
      alert('Dealer not found');
      navigate('/network');
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async () => {
    try {
      await connectionAPI.sendRequest(parseInt(dealerId));
      alert('Connection request sent!');
      fetchData();
    } catch (error) {
      console.error('Error sending request:', error);
      alert(error.response?.data?.detail || 'Failed to send request');
    }
  };

  const handleAcceptRequest = async () => {
    try {
      await connectionAPI.acceptRequest(dealer.connection_id);
      alert('Connection accepted!');
      fetchData();
    } catch (error) {
      console.error('Error accepting request:', error);
      alert('Failed to accept request');
    }
  };

  const handleRemoveConnection = async () => {
    if (window.confirm('Remove this connection?')) {
      try {
        await connectionAPI.removeConnection(dealer.connection_id);
        fetchData();
      } catch (error) {
        console.error('Error removing connection:', error);
        alert('Failed to remove connection');
      }
    }
  };

  const getWatchImage = (watch) => {
    if (watch.images) {
      let images = watch.images;
      if (typeof images === 'string') {
        try {
          images = JSON.parse(images);
        } catch (e) {
          if (images.length > 5) {
            return images.startsWith('http') ? images : `${getServerURL()}/uploads/watches/${images}`;
          }
          return null;
        }
      }
      if (Array.isArray(images) && images.length > 0) {
        const firstImage = images[0];
        return firstImage.startsWith('http') ? firstImage : `${getServerURL()}/uploads/watches/${firstImage}`;
      }
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  if (!dealer) return null;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
        Back
      </button>

      {/* Dealer Header */}
      <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">{dealer.dealer_name}</h1>
            {dealer.company_name && (
              <div className="flex items-center gap-2 text-gray-600 mt-2">
                <Building2 className="w-5 h-5" />
                <span className="text-xl">{dealer.company_name}</span>
              </div>
            )}
            {(dealer.city || dealer.state || dealer.country) && (
              <div className="flex items-center gap-2 text-gray-500 mt-2">
                <MapPin className="w-5 h-5" />
                <span>
                  {dealer.city && dealer.city}
                  {dealer.city && (dealer.state || dealer.country) && ', '}
                  {dealer.state && dealer.state}
                  {dealer.state && dealer.country && ', '}
                  {dealer.country && dealer.country}
                </span>
              </div>
            )}

            {/* Contact Info - Only visible when connected */}
            {dealer.is_connected && (
              <div className="mt-4 p-4 bg-green-50 rounded-xl border-2 border-green-100">
                <p className="text-xs font-semibold text-green-700 mb-2">Contact Information</p>
                {dealer.email && (
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="w-4 h-4 text-gray-600" />
                    <a
                      href={`mailto:${dealer.email}`}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {dealer.email}
                    </a>
                  </div>
                )}
                {dealer.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-600" />
                    <a
                      href={`tel:${dealer.phone}`}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {dealer.phone}
                    </a>
                  </div>
                )}
              </div>
            )}

            <p className="text-sm text-gray-400 mt-3">
              Member since {new Date(dealer.created_at).toLocaleDateString()}
            </p>
          </div>

          {/* Connection Actions */}
          <div>
            {dealer.connection_status === 'Accepted' ? (
              <div className="text-right">
                <div className="flex items-center gap-2 text-green-600 font-semibold mb-3">
                  <UserCheck className="w-5 h-5" />
                  Connected
                </div>
                <button
                  onClick={handleRemoveConnection}
                  className="px-6 py-2 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all"
                >
                  Remove Connection
                </button>
              </div>
            ) : dealer.connection_status === 'Pending' ? (
              dealer.is_incoming_request ? (
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleAcceptRequest}
                    className="px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-all"
                  >
                    Accept Request
                  </button>
                  <button className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all">
                    Decline
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-yellow-600 font-semibold">
                  <Clock className="w-5 h-5" />
                  Request Sent
                </div>
              )
            ) : (
              <button
                onClick={handleSendRequest}
                className="px-8 py-3 bg-slate-900 text-white rounded-xl font-semibold hover:bg-slate-800 transition-all flex items-center gap-2"
              >
                <UserPlus className="w-5 h-5" />
                Connect
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Marketplace Listings */}
      <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
        <div className="flex items-center gap-3 mb-6">
          <Package className="w-6 h-6 text-slate-900" />
          <h2 className="text-2xl font-bold text-gray-900">
            Marketplace Listings ({listings.length})
          </h2>
        </div>

        {listings.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>No marketplace listings yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((watch) => {
              const imageUrl = getWatchImage(watch);
              return (
                <div
                  key={watch.id}
                  onClick={() => navigate(`/marketplace/${watch.id}`)}
                  className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl overflow-hidden border-2 border-gray-100 hover:border-slate-300 transition-all cursor-pointer group"
                >
                  {/* Watch Image */}
                  <div className="relative h-64 bg-gradient-to-br from-gray-200 to-gray-300 overflow-hidden">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={`${watch.brand} ${watch.model}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <Package className="w-20 h-20" />
                      </div>
                    )}
                    {watch.status && watch.status !== 'Available' && (
                      <div className="absolute top-4 right-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        {watch.status}
                      </div>
                    )}
                  </div>

                  {/* Watch Info */}
                  <div className="p-5">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {watch.brand}
                    </h3>
                    <p className="text-gray-600 mb-3">{watch.model}</p>
                    {watch.reference && (
                      <p className="text-sm text-gray-500 mb-3">Ref: {watch.reference}</p>
                    )}
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-3xl font-bold text-slate-900">
                        ${parseFloat(watch.asking_price || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      {watch.year && (
                        <span>{watch.year}</span>
                      )}
                      {watch.condition && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span>{watch.condition}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DealerProfilePage;
